import * as R from 'ramda';

export const DELTA_SYNC_STATUS_LABELS = {
  pending: 'Pending',
  appended: 'Appended',
  dry_write: 'Dry write',
  triggered: 'Triggered',
  skipped_concurrent: 'Already running',
  trigger_failed: 'Failed',
  append_failed: 'Append failed',
};

export const DELTA_SYNC_LAST_RESULT_REASON_LABELS = {
  no_eligible_company: 'Company has no Itinerary Assist billing subscription',
  inverted_window: 'Sync window is invalid (start is after end)',
  lock_lost: 'Sync lock was lost or taken over',
  fatal_error: 'Unexpected error during company delta sync',
};

export const COMPANY_DELTA_SYNC_SECTION_HEADER = 'Company Delta Sync';

const ISO_DATE_TIME_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,9})?(Z|[+-](\d{2}):(\d{2}))$/;

export const formatLocalDateTime = value => {
  if (typeof value !== 'string' || value === '') return null;
  const match = value.match(ISO_DATE_TIME_PATTERN);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const offsetHour = match[8] ? Number(match[8]) : 0;
  const offsetMinute = match[9] ? Number(match[9]) : 0;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [0, 31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  if (
    month < 1 || month > 12
    || day < 1 || day > daysInMonth[month]
    || hour > 23 || minute > 59 || second > 59
    || offsetHour > 14 || offsetMinute > 59
    || (offsetHour === 14 && offsetMinute !== 0)
  ) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

export const normalizeGondolaRunStatus = value => typeof value === 'string'
  ? value.toLowerCase().replace(/\s+/g, '_')
  : '';

const USER_PAUSE_REASONS = new Set([
  'pause_requested',
  'force_stop',
  'user_requested',
  'user_stopped',
]);

export const getGondolaRunPresentation = source => {
  const run = safeObject(source);
  const status = normalizeGondolaRunStatus(run.status);
  const action = normalizeGondolaRunStatus(run.action);
  const phase = normalizeGondolaRunStatus(run.phase);
  const stoppingReason = normalizeGondolaRunStatus(run.stoppingReason);
  const interruptionReason = normalizeGondolaRunStatus(run.interruptionReason);
  const promotionStatus = normalizeGondolaRunStatus(run.promotionStatus || run.promotion_status);
  const processedValue = run.processedCount ?? run.pairsProcessed ?? run.completedCount;
  const totalValue = run.totalCount ?? run.pairsTotal ?? run.inputCount;
  const processedCount = Number.isFinite(processedValue) && processedValue >= 0
    ? processedValue
    : null;
  const totalCount = Number.isFinite(totalValue) && totalValue >= 0
    ? totalValue
    : null;
  const progressLabel = processedCount != null && totalCount != null
    ? `${processedCount}/${totalCount} processed`
    : null;

  let state = status || 'unknown';
  let label = status ? status.replace(/_/g, ' ') : 'Unknown';
  let color = 'text.primary';
  let showLoader = false;

  if (status === 'running' && phase === 'queued') {
    state = 'queued';
    label = 'Queued';
    showLoader = true;
  } else if (action === 'promote_checkpoint') {
    state = 'promoting';
    label = 'Promoting';
    showLoader = true;
  } else if (action === 'discard_checkpoint') {
    state = 'discarding';
    label = 'Discarding';
    showLoader = true;
  } else if (action === 'resume' && ['resuming', 'restoring', 'setup'].includes(phase)) {
    state = 'resuming';
    label = 'Resuming';
    showLoader = true;
  } else if (status === 'running' || status === 'pending' || status === 'started') {
    state = 'running';
    label = 'Running';
    showLoader = true;
  } else if (status === 'pausing') {
    state = 'pausing';
    label = 'Pausing';
    showLoader = true;
  } else if (status === 'paused' || status === 'promotion_failed') {
    const interrupted = Boolean(interruptionReason)
      || (!USER_PAUSE_REASONS.has(stoppingReason) && Boolean(run.error));
    state = interrupted ? 'interrupted' : 'paused';
    label = interrupted ? 'Interrupted' : 'Paused';
    color = 'warning.main';
  } else if (status === 'ready_to_promote') {
    state = 'ready_to_promote';
    label = 'Ready to promote';
  } else if (status === 'promoted') {
    state = 'promoted';
    label = 'Promoted';
    color = 'success.main';
  } else if (status === 'discarded') {
    state = 'discarded';
    label = 'Discarded';
    color = 'text.secondary';
  } else if (status === 'failed' || status === 'failure' || status === 'error') {
    state = 'failed';
    label = 'Failed';
    color = 'error';
  } else if (status === 'reporting_incomplete') {
    state = 'reporting_incomplete';
    label = 'Reporting incomplete';
    color = 'warning.main';
  } else if (status === 'cancelled' || status === 'canceled') {
    state = 'cancelled';
    label = 'Cancelled';
    color = 'warning.main';
  } else if (status === 'completed') {
    state = 'completed';
    label = 'Completed';
    color = 'success.main';
  }

  if (promotionStatus === 'failed'
      && !['promote_checkpoint', 'discard_checkpoint'].includes(action)) {
    label = `${label} · Promotion failed`;
    color = 'error';
  }

  return {
    action,
    color,
    label,
    phase,
    processedCount,
    progressLabel: state === 'queued' ? null : progressLabel,
    showLoader,
    state,
    status,
    totalCount,
  };
};

export const getScopeRunTimes = scope => {
  const data = safeObject(scope);
  const lastRun = safeObject(data.lastRun);
  const lastPromotion = safeObject(data.lastPromotion);
  const candidates = [
    ['completedAt', 'Last run completed'],
    ['startedAt', 'Last run started'],
    ['date', 'Last run recorded'],
  ];
  const runTime = candidates.reduce((result, [key, label]) => {
    if (result) return result;
    const value = formatLocalDateTime(lastRun[key]);
    return value ? { label, value } : null;
  }, null);
  const promotionValue = formatLocalDateTime(lastPromotion.promotedAt);
  return {
    runTime,
    promotionTime: promotionValue
      ? { label: 'Last promotion', value: promotionValue }
      : null,
  };
};

export const getCompanyDeltaSyncButtonLabel = busy => (
  busy ? 'Running Company Delta Sync...' : 'Run Company Delta Sync'
);

/**
 * If the client gave up polling before the server confirmed completion, the
 * run may still genuinely be in progress. Treat that ambiguity the same as a
 * confirmed in-progress status so callers don't mistake stale local state for
 * a real "idle" read and surface a prior run's lastResult in its place.
 */
export const getEffectiveDeltaSyncStatus = (deltaSyncStatus, isAmbiguous) => (
  isAmbiguous && deltaSyncStatus ? { ...deltaSyncStatus, inProgress: true } : deltaSyncStatus
);

/**
 * Prefer live progress while a sync is running. When idle, prefer lastResult so
 * stale progress cannot stick forever; fall back to progress only if lastResult
 * has no scope rows (e.g. a completed run whose lastResult is still empty).
 */
export const getDeltaSyncScopeResults = deltaSyncStatus => {
  if (!deltaSyncStatus) return [];
  const rawProgressScopeResults = R.pathOr([], ['progress', 'scopeResults'], deltaSyncStatus);
  const progressScopeResults = Array.isArray(rawProgressScopeResults)
    ? rawProgressScopeResults.filter(result => result && typeof result === 'object')
    : [];
  if (deltaSyncStatus.inProgress) {
    // Avoid flashing the previous run's chips while a new sync is in progress
    // but live progress has not been written yet.
    return progressScopeResults;
  }
  const rawLastResultScopeResults = R.pathOr([], ['lastResult', 'scopeResults'], deltaSyncStatus);
  const lastResultScopeResults = Array.isArray(rawLastResultScopeResults)
    ? rawLastResultScopeResults.filter(result => result && typeof result === 'object')
    : [];
  if (lastResultScopeResults.length > 0) return lastResultScopeResults;
  return progressScopeResults;
};

/**
 * Company-level lastResult outcome for the Delta Sync accordion.
 * Returns null while in progress, or when there is nothing company-level to report.
 */
export const getCompanyDeltaSyncOutcomeMessage = deltaSyncStatus => {
  if (!deltaSyncStatus || deltaSyncStatus.inProgress) return null;
  const lastResult = deltaSyncStatus.lastResult;
  if (!lastResult) return null;

  if (lastResult.skipped) {
    const detail = DELTA_SYNC_LAST_RESULT_REASON_LABELS[lastResult.reason]
      || lastResult.reason
      || 'No work to do';
    return {
      severity: 'warning',
      message: `Skipped: ${detail}`,
    };
  }

  if (lastResult.aborted) {
    const detail = DELTA_SYNC_LAST_RESULT_REASON_LABELS[lastResult.reason]
      || lastResult.reason
      || 'Stopped early';
    return {
      severity: 'error',
      message: `Failed: ${detail}`,
    };
  }

  if (lastResult.hasRetryableFailures) {
    return {
      severity: 'warning',
      message: 'Completed with retryable failures; the sync window may not have fully advanced',
    };
  }

  if (lastResult.cursorAdvanced === false) {
    return {
      severity: 'warning',
      message: 'Completed but the sync cursor did not advance',
    };
  }

  const scopeResults = Array.isArray(lastResult.scopeResults) ? lastResult.scopeResults : null;
  const noPairs = (lastResult.appendedCount || 0) === 0
    && (lastResult.dryWriteCount || 0) === 0;
  if (scopeResults && scopeResults.length === 0 && noPairs && lastResult.cursorAdvanced === true) {
    return {
      severity: 'info',
      message: 'Completed: no completed itineraries found in the sync window',
    };
  }

  if (scopeResults && scopeResults.length > 0) {
    return {
      severity: 'info',
      message: 'Completed successfully',
    };
  }

  return null;
};

export const ti2HintLabel = (hint, partners) => {
  if (!hint) return '';
  const partner = (partners || []).find(
    p => R.prop('id', p) === hint || R.prop('partnerId', p) === hint,
  );
  return partner ? R.prop('name', partner) || hint : hint;
};

export const getAppKeyLabel = (ak, partners) => {
  const hint = R.prop('hint', ak);
  const partner = (partners || []).find(
    p => R.prop('id', p) === hint || R.prop('partnerId', p) === hint,
  );
  const integration = R.prop('integrationId', ak) || R.prop('app', ak) || '';
  return (partner ? partner.name : hint || '') + (integration ? ' (' + integration + ')' : '');
};

export const safeObject = value => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

export const safeText = (value, fallback = '') => (
  typeof value === 'string' ? value : fallback
);

export const hasTelemetryFields = telemetry => Object.keys(safeObject(telemetry)).length > 0;

export const getDeltaRuleTelemetry = source => safeObject(R.prop('deltaRuleTelemetry', source));

export const getRuleMutationTelemetry = source => safeObject(R.prop('ruleMutationTelemetry', source));

export const isDeltaRunType = type => (
  ['delta', 'delta_run'].includes(String(type || '').toLowerCase())
);

export const hasBlockedReason = telemetry => {
  const reason = safeObject(telemetry).blockedReason;
  return typeof reason === 'string' && reason.length > 0;
};

const RULE_MUTATION_FAILURE_STATUSES = new Set([
  'promotion_failed',
  'compensation_failed',
  'rolled_back',
  'verification_failed',
  'rollback_failed',
  'write_failed',
]);

export const isRuleMutationCommitted = telemetry => {
  const data = safeObject(telemetry);
  return data.publicationStatus === 'committed' && data.commitStatus === 'verified';
};

export const getRuleMutationChipColor = telemetry => {
  const data = safeObject(telemetry);
  if (hasBlockedReason(data)) return 'warning';
  if (isRuleMutationCommitted(data)) return 'success';
  if (
    RULE_MUTATION_FAILURE_STATUSES.has(data.publicationStatus)
    || RULE_MUTATION_FAILURE_STATUSES.has(data.commitStatus)
  ) return 'error';
  return 'default';
};

export const getRuleMutationStatusParts = telemetry => {
  const data = safeObject(telemetry);
  return [data.publicationStatus, data.commitStatus]
    .filter(value => typeof value === 'string' && value)
    .map(status => status.replace(/_/g, ' '));
};

const RULE_MUTATION_NUMERIC_FIELDS = [
  'baselineRuleCount',
  'finalRuleCount',
  'verifiedRuleCount',
  'rulesAdded',
  'rulesUpdated',
  'rulesDeleted',
  'rulesRestored',
  'ruleEventsTotal',
];

export const hasUsableRuleMutationTelemetry = telemetry => {
  const data = safeObject(telemetry);
  return hasBlockedReason(data)
    || getRuleMutationStatusParts(data).length > 0
    || RULE_MUTATION_NUMERIC_FIELDS.some(field => Number.isFinite(data[field]));
};

export const getDeltaRuleRetainedCount = telemetry => {
  const data = safeObject(telemetry);
  return Number.isFinite(data.sandboxRuleCountBeforeCommit)
    ? data.sandboxRuleCountBeforeCommit
    : data.copiedRuleCount;
};

export const getDeltaRuleRetentionText = (telemetry, { includeMinimum = false } = {}) => {
  const data = safeObject(telemetry);
  const live = data.liveRuleCountAtStart;
  const retained = getDeltaRuleRetainedCount(data);
  if (!Number.isFinite(live) || !Number.isFinite(retained)) return null;
  const minimum = data.minimumRequiredRuleCount;
  return `${retained}/${live} rules retained${
    includeMinimum && Number.isFinite(minimum) ? ` · min ${minimum}` : ''
  }`;
};

export const getDeltaRuleStatusText = (telemetry, options) => (
  getDeltaRuleRetentionText(telemetry, options)
);

export const getDeltaRuleChipColor = () => 'default';

export const ACTIVE_INGEST_STATUSES = new Set([
  'pending', 'queued', 'running', 'processing', 'uploading', 'cancelling', 'canceling', 'stopping', 'reserving',
]);

export const PENDING_MANUAL_SELECTION_STATES = new Set(['dispatching', 'dispatched']);

export const isPendingManualSelection = (ingest, jobId) => {
  const pending = ingest && ingest.pendingManualSelection;
  if (!pending || typeof pending !== 'object') return false;
  if (!PENDING_MANUAL_SELECTION_STATES.has(pending.state)) return false;
  if (jobId !== undefined) return pending.jobId === jobId;
  return true;
};

export const isActiveIngest = ingest => {
  if (!ingest) return false;
  const status = typeof ingest.status === 'string' ? ingest.status.toLowerCase() : '';
  return ACTIVE_INGEST_STATUSES.has(status) || isPendingManualSelection(ingest);
};
